import React, {useCallback, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {useSelector} from 'react-redux';
import {
  selectGameResults,
  selectIsGameStarted,
  selectIsInitiativeInterceptedWithIsolationism,
  selectNextInitiativePlayerId,
  selectPhase,
  selectPhaseCompletedPlayers,
  selectPolicyMarket
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {selectHeaderOwnPlayerView} from "../../../utils/gameState/user/selectHeaderOwnPlayerView";
import {checkIfAnyGamePreparationPhase} from "../../../utils/checkIfAnyGamePreparationPhase";
import {PHASES} from "../../../constants/phases";
import {StatWell} from "../../gameUi/StatWell";
import {GAME_PANEL_MATERIALS, GAME_PANEL_SHAPES, GamePanel} from "../../gameUi/GamePanel";
import {LoadedImage} from "../../EmblaCarousel/FactionsCarousel";
import {getPlayerActionsText} from "../../../utils/players/getPlayerActionsText";
import {calcUsedConstructionActions} from "../../../utils/gameState/construction/calcUsedConstructionActions";
import {getPolicyImagePath} from "../../../utils/policy/getPolicyImagePath";
import {getPlayerNextPolicy} from "../../../utils/policy/getPlayerNextPolicy";
import {calcPolicyPrice} from "../../../utils/policy/calcPolicyPrice";
import {selectTurnDisplayedPlayer} from "../../../utils/gameState/supply/parallelSupplySelectors";
import {useTutorialUiFlags} from "../../../utils/tutorial/useTutorialUiFlags";
import {usePolicyMarketSideMenu} from "../../../contexts/PolicyMarketSideMenuContext";
import {usePlayersListSideMenu} from "../../../contexts/PlayersListSideMenuContext";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {joinClassNames} from "../../gameUi/joinClassNames";
import {useDesktopLayout} from "../../../hooks/visuals/useWideScreenLayout";
import {SIDE_MENU_IDS, useSideMenuStack} from "../../../contexts/SideMenuStackContext";
import {useSideMenuEscape} from "../../../hooks/input/useSideMenuEscape";
import {useGameUiPortalTarget} from "../../gameUi/GameUiPortal";
import PlayerStatsDropdown from "../PlayerStatsDropdown/PlayerStatsDropdown";
import './HeaderMineZone.css';

/**
 * Левая зона шапки: в углу — кнопка рынка на своей плите базальта (рынок
 * общий), за ней «мое» на эмали цвета игрока: свои монеты, очки, действия
 * строительства и своя политика (со стрелкой к купленной на следующий
 * раунд; без политики — приглушенное здание правительства). Нажатие на
 * политику открывает только ее карточку. Политика и рынок —
 * только на ≥ 1024 px. Чей игрок — selectHeaderOwnPlayerView; у зрителя
 * своего игрока нет, и в зоне только рынок (ходящего он видит на плашке под
 * его кружком в строке игроков). Портрета своего игрока здесь нет — он один
 * раз в строке игроков; цвет игрока — эмаль зоны.
 * Нажатие на плиту открывает панель статов (PlayerStatsDropdown): из чего
 * складываются доход, действия строительства, отряды. Она и рынок выпадают
 * в одном месте, поэтому открыта только одна из них.
 * Кнопка меню — в правом углу шапки (Header)
 */
const HeaderMineZone = ({isSpectatorMode = false}) => {
  const view = useSelector((state) => selectHeaderOwnPlayerView(state, isSpectatorMode));
  const isGameStarted = useSelector(selectIsGameStarted);
  const gameResults = useSelector(selectGameResults);
  const phase = useSelector(selectPhase);
  const phaseCompletedPlayers = useSelector(selectPhaseCompletedPlayers);
  const nextInitiativePlayerId = useSelector(selectNextInitiativePlayerId);
  const isInitiativeInterceptedWithIsolationism = useSelector(selectIsInitiativeInterceptedWithIsolationism);
  const tutorialUiFlags = useTutorialUiFlags();
  const policyMarket = useSelector(selectPolicyMarket);
  const turnDisplayedPlayer = useSelector(selectTurnDisplayedPlayer);
  // Кнопка подсвечена, пока рынок открыт, даже если поверх лежит список игроков
  const {isMenuOpen: isMarketOpen, toggleMarket, closeMenu: closeMarket} = usePolicyMarketSideMenu();
  const {openCard} = usePlayersListSideMenu();
  const isDesktopLayout = useDesktopLayout();
  const {topSideMenuId, bringSideMenuToFront} = useSideMenuStack();
  // Панель статов выпадает от ресурсов — в корень матча порталом: у шапки
  // filter и clip-path, внутри нее fixed-панель обрезалась бы
  const portalTarget = useGameUiPortalTarget();
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const closeStats = useCallback(() => {
    REGISTRY_SINGLETON.getSoundManager().playSideMenuClick();
    setIsStatsOpen(false);
  }, []);
  useSideMenuEscape(SIDE_MENU_IDS.PLAYER_STATS, isStatsOpen, closeStats);

  // Рынок открылся (кнопкой, ярлыком, обучением) — статы уступают ему место
  useEffect(() => {
    if (isMarketOpen) {
      setIsStatsOpen(false);
    }
  }, [isMarketOpen]);

  // В подготовке ресурсов еще нет, после матча они в окне итогов
  const isStatsShown = Boolean(view) && isGameStarted && !gameResults && !checkIfAnyGamePreparationPhase(phase);
  // Рынок и своя политика в шапке — только на десктопе: на телефоне до
  // верха экрана тянуться неудобно, там они ярлыками у левого края. Главы
  // обучения без темы политик: рынка нет вовсе. Рынок общий — он и у зрителя.
  // Рынок есть уже при размещении стартовых кварталов (как ярлык на
  // телефоне): предложения можно изучить до первого строительства; в
  // редактировании поля партия еще не начата
  const isMarketShown = isGameStarted && !gameResults && phase !== PHASES.FIELD_PREPARATION
    && isDesktopLayout && !tutorialUiFlags.hidePolicyMarket;

  // Ресурсы пропали из шапки (конец партии) — панель статов закрывается с
  // ними, а не остается открытой в стопке панелей
  useEffect(() => {
    if (!isStatsShown) {
      setIsStatsOpen(false);
    }
  }, [isStatsShown]);

  const player = view?.player;
  const ownPolicy = player?.policy;
  // Цены на кнопке рынка — для своего игрока, у зрителя — для ходящего, как
  // в самом рынке (PolicyMarketSideMenu)
  const marketPricePlayer = player || turnDisplayedPlayer;
  const ownNextPolicy = ownPolicy ? getPlayerNextPolicy(player) : null;
  // Действия строительства — пока свой ход строительства впереди (планирование)
  // или идет; после завершения хода тратить их уже не на что
  const isConstructionActionsShown = phase === PHASES.CONSTRUCTION
    && Boolean(player) && !(phaseCompletedPlayers || []).includes(player.id);

  const toggleStats = (event) => {
    event.stopPropagation();
    if (isStatsOpen) {
      closeStats();
      return;
    }
    // Рынок закрывается со своим звуком — второй щелчок не нужен
    if (isMarketOpen) {
      closeMarket();
    } else {
      REGISTRY_SINGLETON.getSoundManager().playSideMenuClick();
    }
    setIsStatsOpen(true);
    bringSideMenuToFront(SIDE_MENU_IDS.PLAYER_STATS);
  };

  // Плита — кнопка панели статов, но не <button>: внутри нее свои кнопки
  // политик (они открывают карточку и дальше нажатие не пускают)
  const handlePlateKeyDown = (event) => {
    // Enter на кнопке политики всплывает сюда — он ее, а не плиты
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleStats(event);
    }
  };

  const handlePolicyClick = (event, policy) => {
    event.stopPropagation();
    REGISTRY_SINGLETON.getSoundManager().playCardOpen();
    openCard(player, policy);
  };

  return (
    <div className={'header-mine-zone'}>
      {/* Рынок — общий, не «мое»: отдельная плита базальта в левом углу, на
          постоянном месте (не сдвигается от счетчика действий и купленной
          политики). Слева на самой плите — иконка рынка, справа в темном
          углублении — миниатюры предложений от дешевой к дорогой (цена —
          позиция + 1). Кнопка — вся плита */}
      {isMarketShown && (
        <GamePanel
          material={GAME_PANEL_MATERIALS.BAR}
          shape={GAME_PANEL_SHAPES.S}
          className={'header-mine-zone_market-plate'}
        >
          <button
            type={'button'}
            className={joinClassNames(
              'header-mine-zone_market-button',
              isMarketOpen && 'header-mine-zone_market-button_opened',
            )}
            onClick={(event) => {
              event.stopPropagation();
              toggleMarket();
            }}
            title={'Рынок политик'}
            aria-label={'Рынок политик'}
            aria-pressed={isMarketOpen}
          >
            <img
              className={'header-mine-zone_market-icon'}
              src={'/img/icons/governance.png'}
              alt={''}
            />
            <span className={'header-mine-zone_market-well'}>
              {policyMarket && policyMarket.map((policy, index) => {
                const price = marketPricePlayer ? calcPolicyPrice(index, marketPricePlayer) : null;
                return (
                  <span key={index} className={'header-mine-zone_market-offer'}>
                    <img
                      className={'header-mine-zone_market-policy'}
                      src={getPolicyImagePath(policy.id)}
                      alt={policy.name}
                    />
                    {/* Цена — монетой в правом нижнем углу, как значок бота
                        у кружка игрока; видна от 1440 px (CSS). Не по
                        карману — монета приглушена, как цена в самом рынке */}
                    {price !== null && (
                      <span
                        className={joinClassNames(
                          'header-mine-zone_market-price',
                          price > marketPricePlayer.coins && 'header-mine-zone_market-price_unaffordable',
                        )}
                        title={'Цена в монетах'}
                      >
                        {price}
                      </span>
                    )}
                  </span>
                );
              })}
            </span>
          </button>
        </GamePanel>
      )}

      {isStatsShown && (
        <GamePanel
          material={GAME_PANEL_MATERIALS.MINE}
          shape={GAME_PANEL_SHAPES.S}
          playerColor={player.color}
          className={joinClassNames(
            'header-mine-zone_plate',
            isStatsOpen && 'header-mine-zone_plate_opened',
          )}
          role={'button'}
          tabIndex={0}
          onClick={toggleStats}
          onKeyDown={handlePlateKeyDown}
          title={'Доход, действия и отряды'}
          aria-label={'Доход, действия и отряды'}
          aria-expanded={isStatsOpen}
        >
          {/* Значения — в темном углублении: на эмали любого цвета числа
              читаются одинаково */}
          <span className={'gui-stat-strip header-mine-zone_stats'}>
            <StatWell
              bare
              icon={'/img/icons/coins.png'}
              value={player.coins}
              income={player.coinsIncome}
              title={'Монеты'}
            />
            <StatWell
              bare
              icon={'/img/icons/win points.png'}
              value={player.winPoints}
              income={player.winPointsIncome}
              title={'Очки победы'}
            />
            {isConstructionActionsShown && (
              <StatWell
                bare
                maskIcon
                icon={'/img/icons/action.png'}
                value={getPlayerActionsText(
                  calcUsedConstructionActions(
                    player, view.pendingBuildings, nextInitiativePlayerId, isInitiativeInterceptedWithIsolationism
                  ),
                  player.constructionPhaseActions
                )}
                title={'Действия строительства'}
              />
            )}
          </span>

          {/* Без своей политики — приглушенное здание правительства, как на
              ярлыке рынка на телефоне: политики нет, нажимать не на что */}
          {isMarketShown && !ownPolicy && (
            <span className={'header-mine-zone_no-policy'} title={'Политика не куплена'}>
              <img
                className={'header-mine-zone_no-policy-icon'}
                src={'/img/icons/governance.png'}
                alt={'Политика не куплена'}
              />
            </span>
          )}

          {/* Своя политика и купленная на следующий раунд — одного размера,
              между ними стрелка, как в списке игроков */}
          {isMarketShown && ownPolicy && (
            <span className={'header-mine-zone_policies'}>
              <button
                type={'button'}
                className={'header-mine-zone_policy-button'}
                onClick={(event) => handlePolicyClick(event, ownPolicy)}
                title={ownPolicy.name}
              >
                <LoadedImage
                  className={'header-mine-zone_policy-img'}
                  src={getPolicyImagePath(ownPolicy.id)}
                  alt={ownPolicy.name}
                />
              </button>
              {ownNextPolicy && (
                <>
                  <img
                    className={'header-mine-zone_policy-arrow'}
                    src={'/img/icons/up white.png'}
                    alt={''}
                    aria-hidden={true}
                  />
                  <button
                    type={'button'}
                    className={'header-mine-zone_policy-button'}
                    onClick={(event) => handlePolicyClick(event, ownNextPolicy)}
                    title={`${ownNextPolicy.name} — со следующего раунда`}
                  >
                    <LoadedImage
                      className={'header-mine-zone_policy-img'}
                      src={getPolicyImagePath(ownNextPolicy.id)}
                      alt={ownNextPolicy.name}
                    />
                  </button>
                </>
              )}
            </span>
          )}

        </GamePanel>
      )}

      {isStatsShown && createPortal(
        <PlayerStatsDropdown
          isOpen={isStatsOpen}
          onClose={closeStats}
          isFocused={topSideMenuId === SIDE_MENU_IDS.PLAYER_STATS}
          isSpectatorMode={isSpectatorMode}
        />,
        portalTarget
      )}
    </div>
  );
};

export default HeaderMineZone;
